import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { 
  Upload, 
  X, 
  Plus, 
  Camera,
  Tag
} from 'lucide-react'

interface BusinessListingFormData {
  // Basic Information
  title: string
  description: string
  category: string
  subcategory: string
  
  // Location
  address: string
  city: string
  region: string
  postalCode: string
  coordinates?: { lat: number; lng: number }
  
  // Contact Information
  phone: string
  email: string
  website?: string
  
  // Business Details
  businessHours: {
    [key: string]: { open: string; close: string; closed: boolean }
  }
  services: Array<{
    name: string
    description: string
    price: number
    duration: number
    category: string
  }>
  
  // Media
  images: File[]
  logo?: File
  
  // Features
  features: string[]
  languages: string[]
  paymentMethods: string[]
  
  // Pricing
  priceRange: string
  currency: string
  
  // Additional Information
  tags: string[]
  specialOffers?: string
  emergencyService: boolean
  homeService: boolean
  onlineService: boolean
}

interface BusinessListingFormProps {
  initialData?: Partial<BusinessListingFormData>
  onSubmit: (data: BusinessListingFormData) => Promise<void>
  loading?: boolean
  className?: string
}

export default function BusinessListingForm({ 
  initialData, 
  onSubmit, 
  loading = false,
  className = '' 
}: BusinessListingFormProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  
  const [currentStep, setCurrentStep] = useState(1)
  const [formData, setFormData] = useState<BusinessListingFormData>({
    title: '',
    description: '',
    category: '',
    subcategory: '',
    address: '',
    city: '',
    region: '',
    postalCode: '',
    phone: '',
    email: '',
    website: '',
    businessHours: {
      sunday: { open: '09:00', close: '18:00', closed: false },
      monday: { open: '09:00', close: '18:00', closed: false },
      tuesday: { open: '09:00', close: '18:00', closed: false },
      wednesday: { open: '09:00', close: '18:00', closed: false },
      thursday: { open: '09:00', close: '18:00', closed: false },
      friday: { open: '09:00', close: '13:00', closed: false },
      saturday: { open: '09:00', close: '18:00', closed: true }
    },
    services: [],
    images: [],
    features: [],
    languages: ['hebrew'],
    paymentMethods: ['cash', 'credit_card'],
    priceRange: 'medium',
    currency: 'ILS',
    tags: [],
    emergencyService: false,
    homeService: false,
    onlineService: false,
    ...initialData
  })
  
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [isSubmitting, setIsSubmitting] = useState(false)

  const categories = [
    { value: 'veterinary', label: t('businessListing.categories.veterinary') },
    { value: 'grooming', label: t('businessListing.categories.grooming') },
    { value: 'training', label: t('businessListing.categories.training') },
    { value: 'boarding', label: t('businessListing.categories.boarding') },
    { value: 'walking', label: t('businessListing.categories.walking') },
    { value: 'pet_sitting', label: t('businessListing.categories.petSitting') },
    { value: 'emergency', label: t('businessListing.categories.emergency') },
    { value: 'other', label: t('businessListing.categories.other') }
  ]

  const features = [
    { value: 'parking', label: t('businessListing.features.parking') },
    { value: 'wheelchair_accessible', label: t('businessListing.features.wheelchairAccessible') },
    { value: 'emergency_service', label: t('businessListing.features.emergencyService') },
    { value: 'home_visit', label: t('businessListing.features.homeVisit') },
    { value: 'online_consultation', label: t('businessListing.features.onlineConsultation') },
    { value: 'pet_friendly', label: t('businessListing.features.petFriendly') },
    { value: 'insurance_accepted', label: t('businessListing.features.insuranceAccepted') }
  ]

  const languages = [
    { value: 'hebrew', label: t('businessListing.languages.hebrew') },
    { value: 'english', label: t('businessListing.languages.english') },
    { value: 'arabic', label: t('businessListing.languages.arabic') },
    { value: 'russian', label: t('businessListing.languages.russian') }
  ]

  const paymentMethods = [
    { value: 'cash', label: t('businessListing.paymentMethods.cash') },
    { value: 'credit_card', label: t('businessListing.paymentMethods.creditCard') },
    { value: 'bank_transfer', label: t('businessListing.paymentMethods.bankTransfer') },
    { value: 'paypal', label: t('businessListing.paymentMethods.paypal') },
    { value: 'bit', label: t('businessListing.paymentMethods.bit') }
  ]

  const priceRanges = [
    { value: 'low', label: t('businessListing.priceRanges.low') },
    { value: 'medium', label: t('businessListing.priceRanges.medium') },
    { value: 'high', label: t('businessListing.priceRanges.high') },
    { value: 'premium', label: t('businessListing.priceRanges.premium') }
  ]

  const handleInputChange = (field: keyof BusinessListingFormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }))
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }))
    }
  }

  const handleServiceAdd = () => {
    setFormData(prev => ({
      ...prev,
      services: [...prev.services, {
        name: '',
        description: '',
        price: 0,
        duration: 60,
        category: ''
      }]
    }))
  }

  const handleServiceChange = (index: number, field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      services: prev.services.map((service, i) => 
        i === index ? { ...service, [field]: value } : service
      )
    }))
  }

  const handleServiceRemove = (index: number) => {
    setFormData(prev => ({
      ...prev,
      services: prev.services.filter((_, i) => i !== index)
    }))
  }

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || [])
    setFormData(prev => ({
      ...prev,
      images: [...prev.images, ...files]
    }))
  }

  const handleImageRemove = (index: number) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }))
  }

  const validateStep = (step: number): boolean => {
    const newErrors: Record<string, string> = {}

    if (step === 1) {
      if (!formData.title.trim()) newErrors.title = t('businessListing.errors.titleRequired')
      if (!formData.description.trim()) newErrors.description = t('businessListing.errors.descriptionRequired')
      if (!formData.category) newErrors.category = t('businessListing.errors.categoryRequired')
    }

    if (step === 2) {
      if (!formData.address.trim()) newErrors.address = t('businessListing.errors.addressRequired')
      if (!formData.city.trim()) newErrors.city = t('businessListing.errors.cityRequired')
      if (!formData.phone.trim()) newErrors.phone = t('businessListing.errors.phoneRequired')
      if (!formData.email.trim()) newErrors.email = t('businessListing.errors.emailRequired')
    }

    if (step === 3) {
      if (formData.services.length === 0) {
        newErrors.services = t('businessListing.errors.servicesRequired')
      }
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, 4))
    }
  }

  const handlePrevious = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1))
  }

  const handleSubmit = async () => {
    if (validateStep(currentStep)) {
      setIsSubmitting(true)
      try {
        await onSubmit(formData)
        navigate('/business-dashboard')
      } catch (error) {
        console.error('Error submitting form:', error)
      } finally {
        setIsSubmitting(false)
      }
    }
  }

  const renderStep1 = () => (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {t('businessListing.title')} *
        </label>
        <input
          type="text"
          value={formData.title}
          onChange={(e) => handleInputChange('title', e.target.value)}
          className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
            errors.title ? 'border-red-500' : 'border-gray-300'
          }`}
          placeholder={t('businessListing.titlePlaceholder')}
        />
        {errors.title && <p className="text-red-500 text-sm mt-1">{errors.title}</p>}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {t('businessListing.description')} *
        </label>
        <textarea
          value={formData.description}
          onChange={(e) => handleInputChange('description', e.target.value)}
          rows={4}
          className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
            errors.description ? 'border-red-500' : 'border-gray-300'
          }`}
          placeholder={t('businessListing.descriptionPlaceholder')}
        />
        {errors.description && <p className="text-red-500 text-sm mt-1">{errors.description}</p>}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t('businessListing.category')} *
          </label>
          <select
            value={formData.category}
            onChange={(e) => handleInputChange('category', e.target.value)}
            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
              errors.category ? 'border-red-500' : 'border-gray-300'
            }`}
          >
            <option value="">{t('businessListing.selectCategory')}</option>
            {categories.map(category => (
              <option key={category.value} value={category.value}>
                {category.label}
              </option>
            ))}
          </select>
          {errors.category && <p className="text-red-500 text-sm mt-1">{errors.category}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t('businessListing.subcategory')}
          </label>
          <input
            type="text"
            value={formData.subcategory}
            onChange={(e) => handleInputChange('subcategory', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            placeholder={t('businessListing.subcategoryPlaceholder')}
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {t('businessListing.tags')}
        </label>
        <input
          type="text"
          value={formData.tags.join(', ')}
          onChange={(e) => handleInputChange('tags', e.target.value.split(',').map(tag => tag.trim()))}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          placeholder={t('businessListing.tagsPlaceholder')}
        />
        <p className="text-sm text-gray-500 mt-1">{t('businessListing.tagsHelp')}</p>
      </div>
    </div>
  )

  const renderStep2 = () => (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {t('businessListing.address')} *
        </label>
        <input
          type="text"
          value={formData.address}
          onChange={(e) => handleInputChange('address', e.target.value)}
          className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
            errors.address ? 'border-red-500' : 'border-gray-300'
          }`}
          placeholder={t('businessListing.addressPlaceholder')}
        />
        {errors.address && <p className="text-red-500 text-sm mt-1">{errors.address}</p>}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t('businessListing.city')} *
          </label>
          <input
            type="text"
            value={formData.city}
            onChange={(e) => handleInputChange('city', e.target.value)}
            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
              errors.city ? 'border-red-500' : 'border-gray-300'
            }`}
            placeholder={t('businessListing.cityPlaceholder')}
          />
          {errors.city && <p className="text-red-500 text-sm mt-1">{errors.city}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t('businessListing.region')}
          </label>
          <input
            type="text"
            value={formData.region}
            onChange={(e) => handleInputChange('region', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            placeholder={t('businessListing.regionPlaceholder')}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t('businessListing.postalCode')}
          </label>
          <input
            type="text"
            value={formData.postalCode}
            onChange={(e) => handleInputChange('postalCode', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            placeholder={t('businessListing.postalCodePlaceholder')}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t('businessListing.phone')} *
          </label>
          <input
            type="tel"
            value={formData.phone}
            onChange={(e) => handleInputChange('phone', e.target.value)}
            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
              errors.phone ? 'border-red-500' : 'border-gray-300'
            }`}
            placeholder={t('businessListing.phonePlaceholder')}
          />
          {errors.phone && <p className="text-red-500 text-sm mt-1">{errors.phone}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t('businessListing.email')} *
          </label>
          <input
            type="email"
            value={formData.email}
            onChange={(e) => handleInputChange('email', e.target.value)}
            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
              errors.email ? 'border-red-500' : 'border-gray-300'
            }`}
            placeholder={t('businessListing.emailPlaceholder')}
          />
          {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {t('businessListing.website')}
        </label>
        <input
          type="url"
          value={formData.website}
          onChange={(e) => handleInputChange('website', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          placeholder={t('businessListing.websitePlaceholder')}
        />
      </div>
    </div>
  )

  const renderStep3 = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium text-gray-900">
          {t('businessListing.services')}
        </h3>
        <button
          type="button"
          onClick={handleServiceAdd}
          className="btn-primary flex items-center gap-2"
        >
          <Plus className="h-4 w-4" />
          {t('businessListing.addService')}
        </button>
      </div>

      {formData.services.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          <Tag className="h-12 w-12 mx-auto mb-4 text-gray-300" />
          <p>{t('businessListing.noServices')}</p>
        </div>
      ) : (
        <div className="space-y-4">
          {formData.services.map((service, index) => (
            <div key={index} className="p-4 border border-gray-200 rounded-lg">
              <div className="flex items-center justify-between mb-4">
                <h4 className="font-medium text-gray-900">
                  {t('businessListing.service')} {index + 1}
                </h4>
                <button
                  type="button"
                  onClick={() => handleServiceRemove(index)}
                  className="text-red-500 hover:text-red-700"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('businessListing.serviceName')} *
                  </label>
                  <input
                    type="text"
                    value={service.name}
                    onChange={(e) => handleServiceChange(index, 'name', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    placeholder={t('businessListing.serviceNamePlaceholder')}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('businessListing.serviceCategory')}
                  </label>
                  <input
                    type="text"
                    value={service.category}
                    onChange={(e) => handleServiceChange(index, 'category', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    placeholder={t('businessListing.serviceCategoryPlaceholder')}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('businessListing.servicePrice')} (₪)
                  </label>
                  <input
                    type="number"
                    value={service.price}
                    onChange={(e) => handleServiceChange(index, 'price', parseFloat(e.target.value) || 0)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    placeholder="0"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('businessListing.serviceDuration')} (דקות)
                  </label>
                  <input
                    type="number"
                    value={service.duration}
                    onChange={(e) => handleServiceChange(index, 'duration', parseInt(e.target.value) || 0)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    placeholder="60"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('businessListing.serviceDescription')}
                  </label>
                  <textarea
                    value={service.description}
                    onChange={(e) => handleServiceChange(index, 'description', e.target.value)}
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    placeholder={t('businessListing.serviceDescriptionPlaceholder')}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {errors.services && (
        <p className="text-red-500 text-sm">{errors.services}</p>
      )}
    </div>
  )

  const renderStep4 = () => (
    <div className="space-y-6">
      {/* Business Hours */}
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-4">
          {t('businessListing.businessHours')}
        </h3>
        <div className="space-y-3">
          {Object.entries(formData.businessHours).map(([day, hours]) => (
            <div key={day} className="flex items-center gap-4">
              <div className="w-20 text-sm font-medium text-gray-700">
                {t(`businessListing.days.${day}`)}
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={!hours.closed}
                  onChange={(e) => {
                    const newHours = { ...formData.businessHours }
                    newHours[day] = { ...hours, closed: !e.target.checked }
                    handleInputChange('businessHours', newHours)
                  }}
                  className="rounded border-gray-300"
                />
                <span className="text-sm text-gray-600">
                  {t('businessListing.open')}
                </span>
              </div>
              {!hours.closed && (
                <div className="flex items-center gap-2">
                  <input
                    type="time"
                    value={hours.open}
                    onChange={(e) => {
                      const newHours = { ...formData.businessHours }
                      newHours[day] = { ...hours, open: e.target.value }
                      handleInputChange('businessHours', newHours)
                    }}
                    className="px-2 py-1 border border-gray-300 rounded text-sm"
                  />
                  <span className="text-gray-500">-</span>
                  <input
                    type="time"
                    value={hours.close}
                    onChange={(e) => {
                      const newHours = { ...formData.businessHours }
                      newHours[day] = { ...hours, close: e.target.value }
                      handleInputChange('businessHours', newHours)
                    }}
                    className="px-2 py-1 border border-gray-300 rounded text-sm"
                  />
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Features */}
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-4">
          {t('businessListing.features')}
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {features.map(feature => (
            <label key={feature.value} className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={formData.features.includes(feature.value)}
                onChange={(e) => {
                  const newFeatures = e.target.checked
                    ? [...formData.features, feature.value]
                    : formData.features.filter(f => f !== feature.value)
                  handleInputChange('features', newFeatures)
                }}
                className="rounded border-gray-300"
              />
              <span className="text-sm text-gray-700">{feature.label}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Languages */}
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-4">
          {t('businessListing.languages')}
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {languages.map(language => (
            <label key={language.value} className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={formData.languages.includes(language.value)}
                onChange={(e) => {
                  const newLanguages = e.target.checked
                    ? [...formData.languages, language.value]
                    : formData.languages.filter(l => l !== language.value)
                  handleInputChange('languages', newLanguages)
                }}
                className="rounded border-gray-300"
              />
              <span className="text-sm text-gray-700">{language.label}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Payment Methods */}
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-4">
          {t('businessListing.paymentMethods')}
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {paymentMethods.map(method => (
            <label key={method.value} className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={formData.paymentMethods.includes(method.value)}
                onChange={(e) => {
                  const newMethods = e.target.checked
                    ? [...formData.paymentMethods, method.value]
                    : formData.paymentMethods.filter(m => m !== method.value)
                  handleInputChange('paymentMethods', newMethods)
                }}
                className="rounded border-gray-300"
              />
              <span className="text-sm text-gray-700">{method.label}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Price Range */}
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-4">
          {t('businessListing.priceRange')}
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {priceRanges.map(range => (
            <label key={range.value} className="flex items-center gap-2">
              <input
                type="radio"
                name="priceRange"
                value={range.value}
                checked={formData.priceRange === range.value}
                onChange={(e) => handleInputChange('priceRange', e.target.value)}
                className="border-gray-300"
              />
              <span className="text-sm text-gray-700">{range.label}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Images */}
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-4">
          {t('businessListing.images')}
        </h3>
        <div className="border-2 border-dashed border-gray-300 rounded-lg p-6">
          <div className="text-center">
            <Camera className="h-12 w-12 mx-auto text-gray-400 mb-4" />
            <p className="text-sm text-gray-600 mb-4">
              {t('businessListing.uploadImages')}
            </p>
            <input
              type="file"
              multiple
              accept="image/*"
              onChange={handleImageUpload}
              className="hidden"
              id="image-upload"
            />
            <label
              htmlFor="image-upload"
              className="btn-primary cursor-pointer inline-flex items-center gap-2"
            >
              <Upload className="h-4 w-4" />
              {t('businessListing.chooseImages')}
            </label>
          </div>
        </div>

        {formData.images.length > 0 && (
          <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4">
            {formData.images.map((image, index) => (
              <div key={index} className="relative">
                <img
                  src={URL.createObjectURL(image)}
                  alt={`Upload ${index + 1}`}
                  className="w-full h-24 object-cover rounded-lg"
                />
                <button
                  type="button"
                  onClick={() => handleImageRemove(index)}
                  className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )

  return (
    <div className={`max-w-4xl mx-auto p-6 ${className}`}>
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        {/* Header */}
        <div className="p-6 border-b border-gray-200">
          <h1 className="text-2xl font-bold text-gray-900">
            {t('businessListing.title')}
          </h1>
          <p className="text-gray-600 mt-1">
            {t('businessListing.subtitle')}
          </p>
        </div>

        {/* Progress */}
        <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {[1, 2, 3, 4].map(step => (
                <div key={step} className="flex items-center">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                    step <= currentStep
                      ? 'bg-primary-600 text-white'
                      : 'bg-gray-300 text-gray-600'
                  }`}>
                    {step}
                  </div>
                  {step < 4 && (
                    <div className={`w-8 h-0.5 mx-2 ${
                      step < currentStep ? 'bg-primary-600' : 'bg-gray-300'
                    }`} />
                  )}
                </div>
              ))}
            </div>
            <div className="text-sm text-gray-600">
              {t('businessListing.step', { current: currentStep, total: 4 })}
            </div>
          </div>
        </div>

        {/* Form Content */}
        <div className="p-6">
          {currentStep === 1 && renderStep1()}
          {currentStep === 2 && renderStep2()}
          {currentStep === 3 && renderStep3()}
          {currentStep === 4 && renderStep4()}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-between">
          <button
            type="button"
            onClick={handlePrevious}
            disabled={currentStep === 1}
            className="btn-outline disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {t('businessListing.previous')}
          </button>

          <div className="flex gap-3">
            <button
              type="button"
              onClick={() => navigate('/business-dashboard')}
              className="btn-outline"
            >
              {t('businessListing.cancel')}
            </button>

            {currentStep < 4 ? (
              <button
                type="button"
                onClick={handleNext}
                className="btn-primary"
              >
                {t('businessListing.next')}
              </button>
            ) : (
              <button
                type="button"
                onClick={handleSubmit}
                disabled={isSubmitting || loading}
                className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? t('businessListing.saving') : t('businessListing.save')}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
